<template>
    <div :class="noHeroView ? 'Work--no-hero' : ''">
        <header data-speed="clamp(0.5)" v-if="!noHeroView">
            <GalleryHero :items="itemsForHero" />
        </header>
        <main class="Work__main" id="projects">
            <div class="Work__list-wrapper">
                <div class="Work__list-header">
                    <h2 v-if="workPage.data.title" class="Work__title">
                        <FillTextEffect
                            id="projects"
                            :trigger-container="'#projects'"
                            :text="workPage.data.title"
                            bottom-position="200px"
                        />
                    </h2>
                    <div class="Work__filters">
                        <ResetFilters
                            :show="!!selectedCapability || !!selectedBrand"
                            @clear-selections="
                                (selectedCapability = ''), (selectedBrand = '')
                            "
                        />
                        <BullDropdown
                            v-if="!capabilitiesPending"
                            v-model="selectedCapability"
                            button-text="By capability"
                            :options="capabilityOptions"
                            :open="capabilityMenuOpen"
                            @opened="
                                (capabilityMenuOpen = true),
                                    (brandMenuOpen = false)
                            "
                            @closed="capabilityMenuOpen = false"
                            class="Work__filter"
                        />
                        <BullDropdown
                            v-if="!brandPending"
                            v-model="selectedBrand"
                            button-text="By brand"
                            :options="brandOptions"
                            :open="brandMenuOpen"
                            @opened="
                                (brandMenuOpen = true),
                                    (capabilityMenuOpen = false)
                            "
                            @closed="brandMenuOpen = false"
                            class="Work__filter"
                        />
                    </div>
                </div>
                <ul class="Work__grid" v-if="calculatedCaseStudies.length">
                    <li
                        v-for="caseStudy in calculatedCaseStudies"
                        :key="caseStudy.id"
                        class="Work__grid-item"
                    >
                        <ImageCard :item="caseStudy" :theme="caseStudy.theme" />
                    </li>
                </ul>
                <div v-else class="Work__empty">
                    <div class="Work__empty-wrapper">
                        No case studies to show
                        <CustomButton
                            theme="plain"
                            class="Work__empty-btn"
                            @click="handleResetFilter"
                        >
                            Reset filters
                        </CustomButton>
                    </div>
                </div>
            </div>
            <div
                v-if="
                    currentCaseStudyBatch?.next_page ||
                    selectedCapability ||
                    selectedBrand
                "
                class="Work__pagination-loader"
            >
                <div
                    class="Work__pagination-loader-wrapper"
                    v-if="calculatedCaseStudies.length"
                >
                    <CustomButton @click="handleLoadMore" class="Work__retry">
                        <span v-if="failedToLoadMore">
                            <span v-if="!retrying">Click to retry</span>
                            <span v-else>Retrying...</span>
                        </span>
                        <span v-else-if="caseStudiesPending"> Loading... </span>
                        <span v-else-if="currentCaseStudyBatch?.next_page">
                            + Load more
                        </span>
                        <span v-else> + Discover all work </span>
                    </CustomButton>
                </div>
            </div>
            <SliceZone
                v-if="workPage.data.slices"
                wrapper="div"
                :slices="workPage.data.slices ?? []"
                :components="components"
                class="Work__slice-zone"
            />
        </main>
    </div>
</template>

<script setup>
import { components } from '~/slices'

const prismic = usePrismic()
const router = useRouter()
const route = useRoute()

const selectedCapability = ref(route.query.capability || '')
const selectedBrand = ref(route.query.brand || '')
const currentPage = ref(1)
const allLoadedCaseStudies = ref([])
const caseStudiesPending = ref(false)
const currentCaseStudyBatch = ref()
const failedToLoadMore = ref(false)
const retrying = ref(false)
const capabilityMenuOpen = ref(false)
const brandMenuOpen = ref(false)

const noHeroView = ref(route.query.brand || route.query.capability)

const { data: workPage } = await useAsyncData('[work]', () =>
    prismic.client.getSingle('work', {
        fetchLinks: [
            'case_study.hero_image',
            'case_study.title',
            'case_study.card_title',
            'case_study.brand',
            'case_study.capability_tags',
            'capability.capability_name',
            'brand.client_name',
            'brand.client_logo',
        ],
    }),
)

const { data: latestCaseStudies } = await useAsyncData(
    '[latestCaseStudies]',
    () => {
        return prismic.client.getByType('case_study', {
            orderings: [
                {
                    field: 'document.first_publication_date',
                    direction: 'desc',
                },
                {
                    field: 'my.case_study.original_publish_date',
                    direction: 'desc',
                },
            ],
            fetchLinks: [
                'capability.capability_name',
                'brand.client_name',
                'brand.client_logo',
                'theme.theme_base_colour',
                'theme.theme_chip_colour',
                'theme.theme_logo',
                'theme.theme_icon',
                'theme.theme_base_icon_colour',
                'theme.theme_chip_icon_colour',
                'theme.theme_text_colour',
            ],
            pageSize: 3,
        })
    },
)

const { data: allCapabilities, pending: capabilitiesPending } =
    await useAsyncData('[allCapabilities]', () => {
        return prismic.client.getAllByType('capability', {
            orderings: {
                field: 'my.capability.capability_name',
                direction: 'asc',
            },
        })
    })

const { data: allBrands, pending: brandPending } = await useAsyncData(
    '[allBrands]',
    () => {
        return prismic.client.getAllByType('brand', {
            orderings: {
                field: 'my.brand.client_name',
                direction: 'asc',
            },
        })
    },
)

const capabilityOptions = computed(() => {
    return allCapabilities.value
        .map((capability) => ({
            id: capability.id,
            value: capability.uid,
            displayValue: capability.data
                ? capability.data.capability_name
                : '',
            selected: selectedCapability.value === capability.uid,
        }))
        .filter((option) => option.displayValue)
})

const selectedCapabilityOption = computed(() => {
    return capabilityOptions.value.find((capability) => capability.selected)
})

const brandOptions = computed(() => {
    return allBrands.value.map((brand) => ({
        id: brand.id,
        value: brand.uid,
        displayValue: brand.data.client_name,
        selected: selectedBrand.value === brand.uid,
    }))
})

const selectedBrandOption = computed(() => {
    return brandOptions.value.find((brand) => brand.selected)
})

const handleLoadMore = () => {
    if (failedToLoadMore.value) {
        retrying.value = true
    } else if (currentCaseStudyBatch.value?.next_page) {
        incrementPage()
    } else {
        selectedCapability.value = ''
        selectedBrand.value = ''
        currentPage.value = 1
        nextTick(() => {
            const { gsap } = useGsap()
            gsap.to(window, { duration: 0.5, scrollTo: { y: '#projects' } })
        })
    }
}

const loadCaseStudies = async (reset = false) => {
    caseStudiesPending.value = true

    return prismic.client
        .get({
            filters: [
                prismic.filter.at('document.type', 'case_study'),
                ...(selectedCapabilityOption.value
                    ? [
                          prismic.filter.at(
                              'my.case_study.capability_tags.capability',
                              selectedCapabilityOption.value.id,
                          ),
                      ]
                    : []),
                ...(selectedBrandOption.value
                    ? [
                          prismic.filter.at(
                              'my.case_study.brand',
                              selectedBrandOption.value.id,
                          ),
                      ]
                    : []),
            ],
            orderings: [
                {
                    field: 'my.case_study.original_publish_date',
                    direction: 'desc',
                },
                {
                    field: 'document.first_publication_date',
                    direction: 'desc',
                },
            ],
            fetchLinks: [
                'capability.capability_name',
                'brand.client_name',
                'brand.client_logo',
                'theme.theme_base_colour',
                'theme.theme_chip_colour',
                'theme.theme_logo',
                'theme.theme_icon',
                'theme.theme_base_icon_colour',
                'theme.theme_chip_icon_colour',
                'theme.theme_text_colour',
            ],
            page: currentPage.value,
            pageSize: 8,
        })
        .then((caseStudiesResponse) => {
            failedToLoadMore.value = false
            currentCaseStudyBatch.value = caseStudiesResponse

            if (!reset) {
                allLoadedCaseStudies.value.push(...caseStudiesResponse.results)
            } else {
                allLoadedCaseStudies.value = caseStudiesResponse.results
            }

            caseStudiesPending.value = false
        })
        .catch(() => {
            if (retrying.value) {
                setTimeout(() => {
                    retrying.value = false
                    failedToLoadMore.value = true
                }, 1000)
            } else {
                retrying.value = false
                failedToLoadMore.value = true
            }

            caseStudiesPending.value = false
        })
}

if (!currentCaseStudyBatch.value) await loadCaseStudies()

function getTagsForCaseStudy(capabilities) {
    return capabilities
        .map((item) => {
            if (!item.capability.data) return
            return item.capability.data.capability_name
        })
        .filter((a) => a)
}

const calculatedCaseStudies = computed(() => {
    return allLoadedCaseStudies.value.map((caseStudy) => {
        return {
            image: {
                path: caseStudy.data.hero_image.small?.url,
                dimensions: caseStudy.data.hero_image.small?.dimensions,
            },
            title: caseStudy.data.card_title ?? caseStudy.data.title,
            tags: getTagsForCaseStudy(caseStudy.data.capability_tags),
            logo: caseStudy.data.brand.data && {
                path: caseStudy.data.brand.data.client_logo.url,
                dimensions: caseStudy.data.brand.data.client_logo.dimensions,
            },
            to: caseStudy.url,
            cursorText: 'View<br>Project',
            theme: {
                ...(caseStudy.data.theme || props.theme || {}),
                ignoreThemeColour: true,
            },
        }
    })
})

const normalisedWorkPageLinkedHeroItems = computed(() => {
    if (noHeroView.value) return []

    return workPage.value.data.custom_linked_hero_items.map(
        ({ linked_hero_item }) => {
            return {
                image: linked_hero_item.data.hero_image,
                title:
                    linked_hero_item.data.title ||
                    linked_hero_item.data.card_title,
                logo: linked_hero_item.data?.brand?.data && {
                    ...linked_hero_item.data?.brand?.data?.client_logo,
                },
                tags: linked_hero_item.data.capability_tags
                    .map((item) => {
                        if (!item.capability.data) return
                        return item.capability.data.capability_name
                    })
                    .filter((a) => a),
                galleryShortCutText: linked_hero_item.data?.brand?.data
                    ? linked_hero_item.data?.brand?.data?.client_name
                    : '',
                to: linked_hero_item.url,
            }
        },
    )
})

const normalisedWorkPageCustomHeroItems = computed(() => {
    if (noHeroView.value) return []

    return workPage.value.data.custom_hero_items.map((item) => {
        return {
            image: item.image,
            title: item.title,
            logo: null,
            tags: [item.tag_1, item.tag_2, item.tag_3].filter((a) => a),
            galleryShortCutText: item.linkText,
            to: item.internal_link.url,
            href: item.external_link.url,
        }
    })
})

const normalisedLatestCaseStudies = computed(() => {
    if (noHeroView.value) return []

    return latestCaseStudies.value.results.map((item) => {
        return {
            image: item.data.hero_image,
            title: item.data.title ?? item.data.card_title,
            logo: item.data.brand.data && {
                ...item.data.brand.data.client_logo,
            },
            tags: item.data.capability_tags
                .map((item) => {
                    if (!item.capability.data) return
                    return item.capability.data.capability_name
                })
                .filter((a) => a),
            galleryShortCutText: item.data?.brand?.data
                ? item.data.brand.data.client_name
                : '',
            to: item.url,
        }
    })
})

const itemsForHero = computed(() => {
    if (noHeroView.value) return []

    return [
        ...normalisedWorkPageLinkedHeroItems.value,
        ...normalisedWorkPageCustomHeroItems.value,
        ...normalisedLatestCaseStudies.value,
    ].slice(0, 3)
})

function incrementPage() {
    currentPage.value += 1
}

function handleResetFilter() {
    const router = useRouter()
    router.push({ query: null })
    selectedCapability.value = ''
    selectedBrand.value = ''
}

useSeoMeta({
    title:
        workPage.value?.data?.meta_title ||
        'eight&four | A Performance Driven Social and Content Agency',
    description:
        workPage.value?.data?.meta_description ||
        "We're a social & end-to-end content production agency. We work with global brands looking for a dynamic, multi-lingual agency.",
    ogTitle:
        workPage.value?.data?.meta_title ||
        'eight&four | A Performance Driven Social and Content Agency',
    ogDescription:
        workPage.value?.data?.meta_description ||
        "We're a social & end-to-end content production agency. We work with global brands looking for a dynamic, multi-lingual agency.",
    ogImage:
        workPage.value?.data?.social_image?.url ||
        process.env.BASE_URL + '/images/default-og-image.png',
})

onMounted(() => {
    const { smoothScroller } = useGsap()

    smoothScroller?.effects('[data-speed]')
})

watch(
    [selectedCapability, selectedBrand],
    async ([newSelectedCapability, newSelectedBrand]) => {
        currentPage.value = 1

        capabilityMenuOpen.value = false
        brandMenuOpen.value = false

        router.push({
            query: {
                ...(newSelectedCapability && {
                    capability: newSelectedCapability,
                }),
                ...(newSelectedBrand && { brand: newSelectedBrand }),
            },
            replace: true,
        })

        return loadCaseStudies(true)
    },
)

watch([currentPage], async (newValue) => {
    return loadCaseStudies(newValue === 1)
})

watch([retrying], async (newValue) => {
    if (newValue) return loadCaseStudies()

    return
})
</script>

<style lang="scss" scoped>
.Work--no-hero {
    margin-top: var(--nav-offset);

    @include large-up {
        margin-top: calc(var(--nav-offset) + var(--promo-bar-height));
    }

    .Work__title {
        display: block;
    }
}

.Work__main {
    @include grid;

    position: relative;
    background-color: var(--black);
    z-index: 1;
}

.Work__list-wrapper {
    grid-column: 2 / span 22;
    margin-bottom: 8rem;

    @include medium-up {
        margin-bottom: 10rem;
    }
}

.Work__title {
    @include heading-2;

    @include small-only {
        display: none;
    }
}

.Work__list-header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: 1rem;
    column-gap: 2rem;
    margin-top: 2.5rem;
    margin-bottom: 2.5rem;

    @include small-only {
        flex-direction: column;
    }

    @include medium-up {
        margin-top: 5rem;
        margin-bottom: 5rem;
    }
}

.Work__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    @include small-only {
        width: 100%;
    }
}

.Work__filter {
    min-width: 15rem;
    width: calc(50% - 0.5rem);

    @include small-only {
        width: 100%;
    }
}

.Work__grid {
    @include grid($columns: 22);

    row-gap: 5rem;
}

.Work__grid-item {
    grid-column: span 22;

    &:nth-child(odd) {
        @include large-up {
            grid-column: 1 / span 10;
        }
    }

    &:nth-child(even) {
        @include large-up {
            grid-column: 13 / span 10;
            margin-top: 2.5rem;
        }
    }
}

.Work__pagination-loader {
    grid-column: 2 / span 22;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10rem;
}

.Work__pagination-loader-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.Work__retry {
    margin-top: 1rem;
}

.Work__slice-zone {
    @include grid;

    grid-column: 1 / span 24;

    > :first-child {
        padding-top: 0;
    }
}

.Work__empty-wrapper {
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid var(--black-30);
    border-radius: 2px;
    padding: 4rem 2rem;
    text-align: center;
    color: var(--black-50);
    margin-top: 10rem;
}

.Work__empty-btn {
    display: block;
    margin-left: auto;
    margin-right: auto;
    margin-top: 0.75rem;
}
</style>
